import React, { useCallback, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Camera, CheckCircle2, ChevronDown, Loader2, LockOpen, MapPin, PackageOpen, Printer, TriangleAlert, Truck, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import FrsTable from "./FrsTable";
import { ReopenPrompt } from "./TicketEditActions";
import SignaturePad from "./SignaturePad";
import DropLocationPicker from "./DropLocationPicker";
import { DeliveryPhotoStrip, PhotosRequiredDialog } from "./DeliveryPhotos";

// FLD-MOB §5 — ONE name per function, phone and desktop alike.
const MODES = [
  { key: "bulk", label: "Bulk drop", hint: "I left all of this in one place" },
  { key: "spread", label: "Spread", hint: "I distributed this to the rooms" },
  { key: "short", label: "Not delivered", hint: "It never arrived — the foreman re-requests it" },
];
const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fallback; };

// Per-ticket session memory — a different ticket always starts clean (no filter bleed).
const viewKey = (id) => `delivery-view-${id}`;
const loadView = (id) => { try { return JSON.parse(sessionStorage.getItem(viewKey(id)) || "{}"); } catch { return {}; } };

/**
 * FLD-3 — THE Delivery view. It is the Install → Material view (`FrsTable` in material mode,
 * same grid, tree, filters, search, selection, full screen and mobile rules) fed by ONE ticket's
 * records instead of the whole project. The delivery-only parts — modes, Place / move, Not
 * Delivered reasons, reopen, sign-off — ride in the same toolbar and below the grid; there is no
 * second toolbar and no second Delivery interface. Placing saves immediately and every line stays
 * editable until sign-off, so there is no Save Delivery Progress and no pull back.
 *
 * Ticket scope is enforced by the data, not by filters: only the ticket's room-product records
 * are handed to the table, so a Scope / Building / Floor / Area / Room with nothing on the ticket
 * cannot appear. Verify OFCI Onsite and View All Onsite Material stay project-wide inside the
 * table and read/write the same FRS material records as Install → Material.
 */
export default function DeliveryWorkspace({ ticketId, ticketNumber, actor, onClose, onChanged, installers = [] }) {
  const saved = useMemo(() => loadView(ticketId), [ticketId]);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(saved.mode || "spread");
  const [selected, setSelected] = useState(new Set(saved.selected || []));
  const [edits, setEdits] = useState(saved.edits || {});
  const EMPTY_DROP = { building: "", floor: "", area: "", room_key: "", room_label: "", note: "" };
  const [bulk, setBulk] = useState(EMPTY_DROP);
  // FLD-MOB §2/§3 — on a phone the header folds down to a summary and the drop panel only appears
  // when material is actually being placed. Desktop keeps every panel exactly where it was.
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const read = () => setPhone(window.innerWidth < 640);
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    return () => { window.removeEventListener("resize", read); window.removeEventListener("orientationchange", read); };
  }, []);
  const [expanded, setExpanded] = useState(false);
  const [facts, setFacts] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [dropSheet, setDropSheet] = useState(false);
  const lean = phone && expanded;          // expanded on a phone: only the mode controls stay
  const [bulkQty, setBulkQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignee, setAssignee] = useState("");
  const [showShort, setShowShort] = useState(false);
  const [reopenPrompt, setReopenPrompt] = useState(false);
  const [blockers, setBlockers] = useState(null);
  const [receipt, setReceipt] = useState({ signed_name: "", signature: "", photos: [], note: "" });
  const [version, setVersion] = useState(0);
  const [showClose, setShowClose] = useState(false);   // mobile: the sign-off panel folds away   // bumps after every action so the grid re-reads placements

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/delivery/tickets/${ticketId}`);
      setData(d);
      const bd = d.bulk_drop || {};
      setBulk({ building: bd.building || "", floor: bd.floor || "", area: bd.area || "",
                room_key: bd.room_key || "", room_label: bd.room_label || "", note: bd.note || "" });
    } catch (e) { toast.error(errText(e, "Could not open the delivery")); onClose?.(); }
  }, [ticketId, onClose]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    sessionStorage.setItem(viewKey(ticketId), JSON.stringify({ mode, selected: [...selected], edits }));
  }, [ticketId, mode, selected, edits]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const totals = data?.totals || {};
  const t = data?.ticket || {};
  const closed = t.status === "Closed";
  const stage = t.delivery_stage || t.status;

  /** ONE source of truth. The view does NOT build its own rows: `FrsTable` reads the project's FRS
   *  records exactly as the FRS view and Install → Material do, and `keys` only SCOPES that set
   *  down to the room-product records on this ticket. Header naming, hierarchy, level labels and
   *  the product summary therefore come from the FRS records themselves — an RA edit shows up here
   *  the moment it is saved, with nothing to sync. `map` carries the ticket's DELIVERY numbers for
   *  those same records (released / placed / not delivered / to account for). */
  const keys = useMemo(() => new Set(rows.map((r) => r.frs_item_key).filter(Boolean)), [rows]);
  const rowByKey = useMemo(() => new Map(rows.map((r) => [r.frs_item_key, r])), [rows]);
  const map = useMemo(() => Object.fromEntries(rows.map((r) => [r.frs_item_key, {
    released: r.released || 0, placed: r.delivered || 0,
    not_delivered: r.not_delivered || 0, unaccounted: r.unaccounted || 0,
  }])), [rows]);

  // The FRS records the table is actually showing, handed back so selection can travel from an
  // FRS record to its delivery line.
  const [scopeItems, setScopeItems] = useState([]);
  const onScope = useCallback((its) => setScopeItems(its || []), []);
  const idToKey = useMemo(() => new Map(scopeItems.map((it) => [it.id, it.frs_item_key])), [scopeItems]);

  /** Nothing locks down until the delivery is signed for: a placed record can be picked again and
   *  re-placed or moved, exactly like the installer material view. Picking a record takes EVERY
   *  unit on it — what is still to account for plus what is already placed somewhere else — so
   *  "spread everything to one place" really leaves one location. */
  const placedOf = (r) => Math.max((r.placements || []).reduce((s, p) => s + (Number(p.qty) || 0) - (Number(p.consumed_qty) || 0), 0), 0);
  const movableOf = (r) => (r.unaccounted || 0) + placedOf(r);
  const openIds = useMemo(() => (closed
    ? new Set()
    : new Set(scopeItems.filter((it) => rowByKey.has(it.frs_item_key)).map((it) => it.id))),
  [scopeItems, rowByKey, closed]);
  const pickedRows = useMemo(() => {
    const out = new Map();
    if (closed) return [];
    for (const id of selected) {
      const row = rowByKey.get(idToKey.get(id));
      if (row) out.set(row.line_id, row);
    }
    return [...out.values()];
  }, [selected, idToKey, rowByKey, closed]);
  const pickedIds = useMemo(() => pickedRows.map((r) => r.line_id), [pickedRows]);
  const pickedUnits = pickedRows.reduce((s, r) => s + movableOf(r), 0);

  // Offline: the write is queued on the device (202 {queued}) — say so instead of printing
  // "undefined unit(s)" from a response that has not happened yet.
  const after = async (msg, description, res) => {
    if (res?.queued) toast.success("Saved on this device", { description: "It will sync the moment you are back online" });
    else toast.success(msg, { description });
    setSelected(new Set()); setEdits({}); setBlockers(null); setBulkQty("");
    setVersion((v) => v + 1);
    await load(); onChanged?.();
  };
  const aggregate = () => ({ level: "material-grid", path: {}, line_ids: pickedIds });

  const doBulkDrop = async () => {
    if (!pickedIds.length) { toast.info("Tick the lines you dropped there"); return; }
    if (!bulk.building && !bulk.floor && !bulk.area && !bulk.room_key && !bulk.note.trim()) {
      toast.info("Pick a place on the job or type where it was left"); return;
    }
    const partial = bulkQty !== "" && Number(bulkQty) > 0 && Number(bulkQty) < pickedUnits;
    if (bulkQty !== "" && (!Number.isFinite(Number(bulkQty)) || Number(bulkQty) <= 0 || Number(bulkQty) > pickedUnits)) {
      toast.info(`Enter 1 to ${pickedUnits} unit(s)`); return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/delivery/tickets/${ticketId}/bulk-drop`,
        { line_ids: pickedIds, ...bulk, note: bulk.note.trim(), by: actor, aggregate: aggregate(), ...(partial ? { qty: Number(bulkQty) } : {}) });
      await after(`${res.placed} unit(s) bulk-dropped across ${res.lines} line(s)`, res.location, res);
    } catch (e) { toast.error(errText(e, "Could not record the bulk drop")); }
    finally { setBusy(false); }
  };

  const spreadLines = async (ids) => {
    const payload = ids.map((id) => {
      const r = rows.find((x) => x.line_id === id) || {};
      const e = edits[id] || {};
      return { line_id: id, qty: Number(e.qty ?? movableOf(r)), location_type: "room",
               room_key: r.room?.room_key || "", area: "", note: "" };
    }).filter((r) => r.qty > 0);
    if (!payload.length) { toast.info("Tick the rooms you placed"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/delivery/tickets/${ticketId}/spread`, { rows: payload, by: actor, aggregate: aggregate() });
      await after(`${res.placed} unit(s) placed across ${res.lines} line(s)`, "Each one has a location on it", res);
    } catch (e) { toast.error(errText(e, "Could not record the placements")); }
    finally { setBusy(false); }
  };
  const doSpread = () => spreadLines(pickedIds);

  const shortLines = async (ids, reason) => {
    const payload = ids.map((id) => {
      const r = rows.find((x) => x.line_id === id) || {};
      return { line_id: id, qty: Number(r.unaccounted ?? 0), reason: reason || edits[id]?.reason || "" };
    }).filter((r) => r.qty > 0);
    if (!payload.length) { toast.info("Nothing selected is still to account for", { description: "Not delivered only applies to material that never arrived — placed material is corrected with Bulk drop or Spread." }); return; }
    if (payload.some((r) => !r.reason)) { toast.info("Pick the reason for the not-delivered material"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/delivery/tickets/${ticketId}/short`, { rows: payload, by: actor, aggregate: aggregate() });
      await after(`${res.not_delivered} unit(s) recorded as not delivered on ${res.lines} line(s)`, "The foreman has to re-request them", res);
    } catch (e) { toast.error(errText(e, "Could not record the short delivery")); }
    finally { setBusy(false); }
  };
  const doShort = (reason) => shortLines(pickedIds, reason);

  /** What the room window (product cell / LOCATION cell) reads and writes: every line this ticket
   *  carries for that room, placed in it or not. */
  const roomPanel = useMemo(() => ({
    rows, closed, reasons: data?.short_reasons || [],
    spread: (ids) => spreadLines(ids),
    short: (ids, reason) => shortLines(ids, reason),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, closed, data, edits, pickedIds]);

  const setStage = async (s, extra = {}) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/delivery/tickets/${ticketId}/stage`, { by: actor, stage: s, ...extra });
      toast.success(`Delivery → ${res.delivery_stage}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(errText(e, "Could not update the delivery")); }
    finally { setBusy(false); }
  };
  const doReopen = async ({ reason, note }) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/tickets/${ticketId}/reopen`, { by: actor, kind: "delivery", reason, note, confirm: true });
      setReopenPrompt(false);
      toast.success("Delivery reopened for editing", { description: res.message });
      await load(); onChanged?.();
    } catch (e) { toast.error(errText(e, "Could not reopen the delivery")); }
    finally { setBusy(false); }
  };
  const addPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("project_id", t.project_id); fd.append("uploader", actor || ""); fd.append("file", file);
    setBusy(true);
    try {
      const { data: photo } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceipt((c) => ({ ...c, photos: [...c.photos, photo] }));
    } catch (e) { toast.error(errText(e, "Could not upload the photo")); }
    finally { setBusy(false); }
  };
  const [photoGate, setPhotoGate] = useState(null);   // P3-5 — "photos_required" from the sign-off
  const doClose = async (extra = {}) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/delivery/tickets/${ticketId}/close`, {
        by: actor, signed_name: receipt.signed_name.trim() || actor, signature: receipt.signature,
        photos: receipt.photos.map((p) => ({ storage_path: p.storage_path, filename: p.filename, content_type: p.content_type })),
        note: receipt.note.trim(), ...extra });
      setPhotoGate(null);
      toast.success(`${ticketNumber} closed${extra.photos_pending ? " — photos pending" : ""}`, { description: `${res.delivered} placed · ${res.not_delivered} not delivered` });
      onChanged?.(); onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "unaccounted") setBlockers(d);
      if (d?.error === "photos_required") { setPhotoGate(d); return; }
      toast.error(errText(e, "Could not close the delivery"));
    } finally { setBusy(false); }
  };

  const shortRows = rows.filter((r) => (r.still_to_pull || 0) > 0);
  const reopened = t.reopened || null;
  const canClose = (totals.unaccounted_blocking ?? totals.unaccounted) <= 0 && !closed;
  // FLD-MOB §4 — the four controls decide what Place / move does. Spread places straight into the
  // assigned rooms, Bulk drop asks WHERE (the same picker the Install Material view uses, in a sheet
  // on a phone instead of a panel nailed to the header), Not delivered asks for a reason.
  const confirmAction = mode === "bulk"
    ? (phone ? () => setDropSheet(true) : doBulkDrop)
    : mode === "short"
    ? (phone ? () => setDropSheet(true) : () => doShort())
    : doSpread;

  /** Row 1 on a phone — what Place / move will do. */
  const modes = (
    <>
      {MODES.map((m) => (
        <button key={m.key} type="button" onClick={() => setMode(m.key)} aria-pressed={mode === m.key} title={m.hint}
                data-testid={`delivery-mode-${m.key}`}
                className={`h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm font-semibold rounded-md border whitespace-nowrap ${mode === m.key ? "bg-blue-700 text-white border-blue-700" : "bg-white text-gray-700 border-gray-300"}`}>
          {m.label}
        </button>
      ))}
    </>
  );

  /** Row 2 on a phone — the action itself. Placing saves straight away, and every line stays
   *  editable until the ticket is signed, so there is no Save and no Pull back. */
  const actions = (
    <>
      {!closed && (
        <Button size="sm" className="h-8 sm:h-9" disabled={busy || !pickedIds.length} onClick={confirmAction} data-testid="delivery-place-selected">
          <PackageOpen className="w-3.5 h-3.5 mr-1" /> {mode === "short" ? "Record" : "Place / move"} {pickedIds.length}<span className="hidden sm:inline"> line(s)</span>
        </Button>
      )}
    </>
  );

  /** Desktop keeps everything on one line, exactly as before. */
  const toolbar = <>{modes}{actions}</>;

  if (!data) return <div className="p-8 text-center" data-testid="delivery-workspace"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="mx-0 wh-work" data-testid="delivery-workspace">
      {!lean && (
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200 px-2 py-1.5 space-y-1" data-testid="delivery-header">
        <div className="flex sm:flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
          <Breadcrumbs className="shrink-0 text-sm" trail={[
            { label: "Deliveries", to: "/warehouse?view=deliveries", testId: "delivery-back",
              match: (u) => u.pathname === "/warehouse" && !u.searchParams.get("delivery") },
            { label: ticketNumber },
          ]} />
          <span className="text-gray-700 text-sm truncate min-w-0">{t.job_number ? `${t.job_number} · ` : ""}{t.project_name}</span>
          {data.type === "direct_ship" && <span className="px-1.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-800" data-testid="delivery-direct-ship">direct ship</span>}
          {/* The Status control already reads the stage on a phone. */}
          <span className={`px-1.5 rounded text-[10px] font-semibold hidden sm:inline ${closed ? "bg-emerald-100 text-emerald-800" : "bg-indigo-100 text-indigo-800"}`} data-testid="delivery-status">{stage}</span>
          {t.released_with_shortfall && <span className="px-1.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid="delivery-partial-flag">Partial — released with missing material</span>}
          <span className="w-full sm:w-auto sm:ml-auto flex items-center gap-1">
            {closed && !reopenPrompt && (
              <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setReopenPrompt(true)} data-testid="delivery-reopen-open">
                <LockOpen className="w-3 h-3 mr-1" /> Reopen Delivery for Editing
              </Button>
            )}
            {/* Print is not a phone control. */}
            <a className="h-8 px-2 hidden sm:inline-flex items-center gap-1 rounded border border-gray-300 text-xs bg-white" target="_blank" rel="noreferrer"
               href={`${API}/field/delivery/tickets/${ticketId}/receipt`} data-testid="delivery-print"><Printer className="w-3.5 h-3.5" /> Print</a>
          </span>
        </div>
        {/* One tappable line on a phone — ticket facts left, delivery status right. Desktop keeps
            the full facts block and the inline stage buttons. */}
        <div className="sm:hidden flex items-center gap-1">
          <button type="button" onClick={() => setFacts((v) => !v)} data-testid="delivery-facts-toggle"
                  className="flex-1 min-w-0 min-h-[44px] flex items-center gap-1 text-[11px] text-gray-700 text-left">
            <span className="truncate">
              Need by <b className="text-gray-900">{t.need_by || "—"}</b> · {t.assignee?.name || "unassigned"} · {(t.scopes || []).join(", ") || "—"}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${facts ? "rotate-180" : ""}`} />
          </button>
          {!closed && (
            <button type="button" onClick={() => setStageOpen((v) => !v)} data-testid="delivery-stage-toggle"
                    className="shrink-0 max-w-[52%] min-h-[44px] px-2 inline-flex items-center gap-1 rounded border border-gray-300 bg-white text-[12px] font-semibold">
              <Truck className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{stage}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${stageOpen ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <div className={`${facts ? "flex" : "hidden"} sm:flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-600`} data-testid="delivery-facts">
          <span>Need by <b className="text-gray-900">{t.need_by || "—"}</b></span>
          <span>Requested delivery <b className="text-gray-900">{t.requested_delivery_date || t.need_by || "—"}</b></span>
          <span>Address <b className="text-gray-900">{t.address || "—"}</b></span>
          {t.instructions && <span>Instructions <b className="text-gray-900">{t.instructions}</b></span>}
          <span>Foreman <b className="text-gray-900">{t.foreman?.name || "not assigned"}</b></span>
          <span>Delivery by <b className="text-gray-900">{t.assignee?.name || "unassigned"}</b></span>
          <span>Scopes <b className="text-gray-900">{(t.scopes || []).join(", ") || "—"}</b></span>
        </div>
        <div className="hidden sm:block text-[11px] text-gray-600" data-testid="delivery-editable-note">
          {closed
            ? "Signed for — reopen the delivery to correct a placement."
            : "Nothing is locked: pick any record again to re-place it or move it somewhere else, right up to sign-off."}
        </div>
        {!closed && (
          <div className={`${stageOpen ? "flex" : "hidden"} sm:flex flex-wrap items-center gap-1 text-[11px]`} data-testid="delivery-stage-controls">
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="h-7 text-[11px] border border-gray-300 rounded max-w-[180px]" data-testid="delivery-assignee">
              <option value="">— delivery employee —</option>
              {installers.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <button disabled={busy || !assignee} onClick={() => setStage("Assigned", { assignee_id: assignee })} className="h-7 px-2 rounded border border-gray-300 bg-white disabled:opacity-40" data-testid="delivery-stage-assigned">Assign</button>
            {["In Transit", "Arrived"].map((s) => (
              <button key={s} disabled={busy || stage === s} onClick={() => setStage(s)} className={`h-7 px-2 rounded border ${stage === s ? "bg-blue-700 text-white border-blue-700" : "bg-white border-gray-300"}`} data-testid={`delivery-stage-${s.toLowerCase().replace(" ", "-")}`}>{s}</button>
            ))}
            <button disabled={busy} onClick={() => { if (window.confirm("Cancel this delivery? The ticket stays on the picking board.")) setStage("Cancelled"); }} className="h-7 px-2 rounded border border-red-300 text-red-700 bg-white" data-testid="delivery-stage-cancelled">Cancel delivery</button>
          </div>
        )}
      </div>
      )}

      {!lean && t.released_with_shortfall && (
        <div className="mx-2 mt-2 border border-amber-300 bg-amber-50 rounded p-2 text-xs text-amber-900" data-testid="delivery-shortfall-warning">
          <div className="flex items-center gap-2">
            <TriangleAlert className="w-4 h-4" />
            <span>This delivery was released with missing material. Review the shortfall list before delivery.</span>
            <button onClick={() => setShowShort((v) => !v)} className="ml-auto underline" data-testid="delivery-shortfall-toggle">{showShort ? "Hide" : "Show"} shortfall</button>
          </div>
          {showShort && (
            <ul className="mt-1 divide-y divide-amber-200" data-testid="delivery-shortfall-list">
              {shortRows.map((r) => (
                <li key={r.line_id} className="py-0.5 flex flex-wrap gap-x-3">
                  <b>{r.product_short}</b><span>{r.room?.label}</span>
                  <span>requested {r.original_requested} · released {r.released} · still to pull <b>{r.still_to_pull}</b></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!lean && data.photo_targets && (
        <DeliveryPhotoStrip ticketId={ticketId} projectId={t.project_id} actor={actor} targets={data.photo_targets} pins={data.pins || []} onChanged={load} />
      )}
      {data.photos_pending && closed && (
        <div className="mx-2 mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="delivery-photos-pending">
          <b>Photos pending</b> — this delivery was signed off before every location had a photo. Add them above.
        </div>
      )}
      <PhotosRequiredDialog gate={photoGate} onClose={() => setPhotoGate(null)} onPending={() => doClose({ photos_pending: true })} />
      {reopened && (
        <div className="mx-2 mt-2 border border-amber-300 bg-amber-50 rounded px-2 py-1 text-[11px] text-amber-900" data-testid="delivery-reopened-banner">
          <b>Reopened for Editing</b> by {reopened.by} — {reopened.reason}. The signed delivery, its signature and every
          original placement are kept in history.
        </div>
      )}

      {mode === "bulk" && !closed && !phone && (
        <div className="mx-2 mt-2 flex flex-wrap items-end gap-2 bg-blue-50 border border-blue-200 rounded p-2" data-testid="delivery-bulk-panel">
          <DropLocationPicker projectId={t.project_id} value={bulk} onChange={setBulk} />
          <label className="text-[10px] uppercase tracking-wide text-gray-600 flex-1 min-w-[200px]">Where exactly
            <Input value={bulk.note} data-testid="delivery-bulk-note" placeholder="north stairwell landing · gang box by elevator 2"
                   onChange={(e) => setBulk((c) => ({ ...c, note: e.target.value }))} className="h-9 text-xs" />
          </label>
          <label className="text-[10px] uppercase tracking-wide text-gray-600">Units dropped
            <Input type="number" min="1" max={pickedUnits || undefined} step="any" value={bulkQty} placeholder={String(pickedUnits)}
                   data-testid="delivery-bulk-qty" onChange={(e) => setBulkQty(e.target.value)} className="h-9 w-20 text-xs text-right" />
          </label>
          <Button size="sm" className="h-9" disabled={busy || !pickedIds.length} onClick={doBulkDrop} data-testid="delivery-bulk-confirm">
            <MapPin className="w-3 h-3 mr-1" /> Drop {bulkQty !== "" && Number(bulkQty) < pickedUnits ? `${Number(bulkQty)} of ` : ""}{pickedUnits} unit(s) · {pickedIds.length} record(s) here
          </Button>
        </div>
      )}
      {mode === "short" && !closed && !phone && (
        <div className="mx-2 mt-2 flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded p-2" data-testid="delivery-short-panel">
          <span className="text-[11px] text-red-800">Not delivered is not "placed somewhere else" — it means the material is not on site and has to be re-requested.</span>
          <select className="h-9 text-[11px] border border-red-300 rounded px-1 bg-white ml-auto" defaultValue="" data-testid="delivery-short-reason-all"
                  onChange={(e) => { if (e.target.value) { doShort(e.target.value); e.target.value = ""; } }}>
            <option value="">Reason for all {pickedIds.length} selected…</option>
            {(data.short_reasons || []).map((x) => <option key={x} value={x}>{String(x).replace(/_/g, " ")}</option>)}
          </select>
        </div>
      )}

      {/* THE view: Install → Material, fed by this ticket's records only. */}
      <FrsTable projectId={t.project_id} installMode installerName={actor}
                delivery={{ keys, map, selected, setSelected, selectable: openIds, toolbar, modes, actions, reload: load,
                            roomPanel,
                            version, onScope, ticketId, onExpanded: setExpanded }} />

      {blockers && (
        <div className="mx-2 mt-2 border border-red-200 bg-red-50 rounded p-2 text-[11px]" data-testid="delivery-blockers">
          <div className="font-semibold text-red-800">{blockers.message}</div>
          <ul className="mt-1 space-y-0.5">
            {(blockers.rows || []).map((r) => <li key={r.line_id} className="text-red-900">{r.product} · {r.room} — <b>{r.unaccounted}</b> to account for</li>)}
          </ul>
        </div>
      )}
      {/* FLD-MOB §4 — Place / move on a phone: Bulk drop asks where (same picker as Install
          Material), Not delivered asks why. Nothing is placed until the sheet is confirmed. */}
      <Dialog open={dropSheet && !closed} onOpenChange={(v) => { if (!v) setDropSheet(false); }}>
        <DialogContent className="max-w-md w-[94vw] p-3 gap-2" data-testid="delivery-drop-sheet">
          <DialogTitle className="text-sm">
            {mode === "bulk" ? "Where did it all go?" : "Why was it not delivered?"}
          </DialogTitle>
          {mode === "bulk" ? (
            <div className="space-y-2">
              <DropLocationPicker projectId={t.project_id} value={bulk} onChange={setBulk} />
              <label className="block text-[10px] uppercase tracking-wide text-gray-600">Where exactly
                <Input value={bulk.note} data-testid="delivery-sheet-note" placeholder="north stairwell landing · gang box by elevator 2"
                       onChange={(e) => setBulk((c) => ({ ...c, note: e.target.value }))} className="h-11 text-sm" />
              </label>
              <label className="block text-[10px] uppercase tracking-wide text-gray-600">Units dropped
                <Input type="number" min="1" max={pickedUnits || undefined} step="any" value={bulkQty} placeholder={String(pickedUnits)}
                       data-testid="delivery-sheet-qty" onChange={(e) => setBulkQty(e.target.value)} className="h-11 w-24 text-sm text-right" />
              </label>
              <Button className="w-full min-h-[44px]" disabled={busy || !pickedIds.length}
                      onClick={async () => { await doBulkDrop(); setDropSheet(false); }} data-testid="delivery-sheet-drop">
                <MapPin className="w-4 h-4 mr-1" /> Drop {bulkQty !== "" && Number(bulkQty) < pickedUnits ? `${Number(bulkQty)} of ` : ""}{pickedUnits} unit(s) here
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[12px] text-red-800">
                Not delivered is not "placed somewhere else" — it means the material is not on site and has to be re-requested.
              </p>
              {(data.short_reasons || []).map((x) => (
                <button key={x} type="button" data-testid={`delivery-sheet-reason-${x}`}
                        className="w-full min-h-[44px] px-3 text-left text-sm rounded border border-red-300 bg-white"
                        onClick={async () => { await doShort(x); setDropSheet(false); }}>
                  {String(x).replace(/_/g, " ")}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {reopenPrompt && (
        <ReopenPrompt title="Reopen this signed delivery?" busy={busy} testId="delivery-reopen-prompt"
                      onCancel={() => setReopenPrompt(false)} onConfirm={doReopen}
                      body="The delivery has been signed for, so placements are read-only. Reopening lets any normal delivery user correct them; a new review and sign-off is required afterwards." />
      )}

      {/* On a phone the sign-off panel used to eat the screen the crew needs for the matrix, so it
          stays folded until it is wanted. Desktop is unchanged. */}
      <button type="button" onClick={() => setShowClose((v) => !v)} data-testid="delivery-close-toggle"
              className="sm:hidden mx-2 my-2 w-[calc(100%-1rem)] h-11 rounded border border-gray-300 bg-gray-50 text-[12px] font-semibold text-gray-700">
        {showClose ? "Hide sign-off" : "Close the delivery — sign-off"}
      </button>
      <div className={`${showClose ? "" : "hidden sm:block"} mx-2 my-2 border border-gray-200 rounded p-2 bg-gray-50 space-y-2`} data-testid="delivery-close-panel">
        <div className="text-[10px] uppercase tracking-wide text-gray-600 font-semibold">Close the delivery — custody passes to the installer</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[11px]" data-testid="delivery-close-summary">
          <span>Released <b>{totals.requested}</b></span><span>Placed <b>{totals.delivered}</b></span>
          <span>Not delivered <b>{totals.not_delivered}</b></span><span>To account for <b>{totals.unaccounted}</b></span>
        </div>
        {!canClose ? (
          <div className="text-[11px] text-red-700" data-testid="delivery-close-locked">
            {closed ? "This ticket is already closed." : `${totals.unaccounted_blocking ?? totals.unaccounted} unit(s) are neither placed nor recorded as not delivered.`}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Input value={receipt.signed_name} className="h-9 text-xs" placeholder="Name of the person receiving it"
                     data-testid="delivery-signed-name" onChange={(e) => setReceipt((c) => ({ ...c, signed_name: e.target.value }))} />
              <SignaturePad testId="delivery-signature" onChange={(sig) => setReceipt((c) => ({ ...c, signature: sig }))} />
            </div>
            <div className="space-y-1">
              <Input value={receipt.note} className="h-9 text-xs" placeholder="Note (optional)" data-testid="delivery-receipt-note"
                     onChange={(e) => setReceipt((c) => ({ ...c, note: e.target.value }))} />
              <label className="inline-flex items-center gap-1 text-[11px] text-blue-700 cursor-pointer" data-testid="delivery-photo-label">
                <Camera className="w-3.5 h-3.5" /> Add a photo of where it was dropped (optional)
                <input type="file" accept="image/*" className="hidden" data-testid="delivery-photo-input"
                       onChange={(e) => { addPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              <div className="flex flex-wrap gap-1">
                {receipt.photos.map((p) => (
                  <span key={p.storage_path} className="relative">
                    <img alt={p.filename} className="w-14 h-14 object-cover rounded border border-gray-300" data-testid={`delivery-photo-${p.storage_path}`}
                         src={`${API}/completion-photos/${p.storage_path}?auth=${encodeURIComponent(localStorage.getItem("auth_token") || "")}`} />
                    <button className="absolute -top-1 -right-1 bg-white rounded-full border border-gray-300"
                            onClick={() => setReceipt((c) => ({ ...c, photos: c.photos.filter((x) => x.storage_path !== p.storage_path) }))}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Button size="sm" className="h-9 w-full" disabled={busy || (!receipt.signature && !(reopened && receipt.note.trim()))}
                      onClick={() => doClose()} data-testid="delivery-close-confirm">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" /> Close delivery</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
